/**
 * utils/brandingHelpers.ts
 *
 * Utilities for sender branding in the public client-facing form experience.
 * Derives display name and initials from optional first_name / last_name fields.
 * No company_name dependency - the app only stores first and last names.
 *
 * Created Apr 2026: extracted from the branding refactor that adds continuous
 *   logo + name display across all client-facing screens.
 */

/**
 * Returns a display name from first_name + last_name (trimmed and joined).
 * Returns null if both are absent or empty - callers should fall back to
 * rendering logo-only or a generic label rather than an invented name.
 */
export function getSenderDisplayName(user: {
  first_name?: string | null;
  last_name?: string | null;
}): string | null {
  const parts = [user.first_name?.trim(), user.last_name?.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * Returns 1 or 2 uppercase initials for the monogram fallback.
 * Returns null when no name info is available.
 */
export function getSenderInitials(user: {
  first_name?: string | null;
  last_name?: string | null;
}): string | null {
  const first = user.first_name?.trim();
  const last = user.last_name?.trim();
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  return null;
}
